import { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { PreviewColumn } from 'Components/PreviewColumn/PreviewColumn'
import { PreviewColumnTypeEnum } from 'Components/PreviewColumn/PreviewColumn.types'
import { PreviewColumnContent } from 'Components/PreviewColumn/PreviewColumnContent'
import { PreviewColumnHeader } from 'Components/PreviewColumn/PreviewColumnHeader'

import { useIsOrganisation } from 'Hooks'

import { getCaseItemTypeTranslationId } from 'Utilities'

import {
  getCaseItemListPreviewColumn,
  getCaseItemListPreviewColumnVariables,
} from 'Constants/graphql/queries/detail/__generated__/getCaseItemListPreviewColumn'
import { GET_CASE_ITEM_LIST_PREVIEW_COLUMN } from 'Constants/graphql/queries/detail/GetCaseItemDetails'

import { composePath } from 'Portal/src/utilities/composePath/composePath.util'

import { OrdersPreviewColumnProps } from './OrdersPreviewColumn.types'
import { generateOrdersPreviewColumnContent } from './OrdersPreviewColumn.utils'

export const OrdersPreviewColumn = (props: OrdersPreviewColumnProps) => {
  const { entityId, onClosePreviewColumn, isOpen } = props
  const intl = useIntl()
  const isOrganisation = useIsOrganisation()
  const { employeeId, organisationId } = useParams<{
    employeeId?: string
    organisationId?: string
  }>()

  const path = composePath({
    isOrganisation,
    params: {
      employeeId,
      orderId: entityId || '0',
      organisationId,
    },
    paths: {
      employee: GLOBAL_ROUTE_PATHS.employee.order,
      organisation: GLOBAL_ROUTE_PATHS.organisation.order,
    },
  })

  const [getOrdersPreviewData, { loading: isLoading, data }] = useLazyQuery<
    getCaseItemListPreviewColumn,
    getCaseItemListPreviewColumnVariables
  >(GET_CASE_ITEM_LIST_PREVIEW_COLUMN)

  const generateContent = useMemo(
    () => generateOrdersPreviewColumnContent(data?.caseItem),
    [data?.caseItem],
  )

  const generateName = useMemo(() => {
    if (
      data?.caseItem.data.requestedRentCategory?.deviceDefinition
        ?.manufacturer &&
      data?.caseItem.data.requestedRentCategory?.deviceDefinition?.codename
    ) {
      return `${data.caseItem.data.requestedRentCategory?.deviceDefinition?.manufacturer} ${data.caseItem.data.requestedRentCategory?.deviceDefinition?.codename}`
    }

    if (data?.caseItem.caseType) {
      const title = getCaseItemTypeTranslationId(data.caseItem.caseType)

      return intl.formatMessage({
        id: title,
      })
    }

    return ''
  }, [
    data?.caseItem.caseType,
    data?.caseItem.data.requestedRentCategory?.deviceDefinition?.codename,
    data?.caseItem.data.requestedRentCategory?.deviceDefinition?.manufacturer,
    intl,
  ])

  const generateTitle = useMemo(() => {
    const requestName =
      data?.caseItem.caseType &&
      data?.caseItem.data.requestedRentCategory?.deviceDefinition
        ? getCaseItemTypeTranslationId(data.caseItem.caseType)
        : intl.formatMessage({
            id: 'Request',
          })

    return intl.formatMessage({
      id: requestName,
    })
  }, [
    data?.caseItem.caseType,
    data?.caseItem.data.requestedRentCategory?.deviceDefinition,
    intl,
  ])

  useEffect(() => {
    if (!entityId) {
      return
    }

    getOrdersPreviewData({
      variables: {
        id: entityId,
      },
    })
  }, [entityId, getOrdersPreviewData])

  return (
    <PreviewColumn
      contentElement={
        <PreviewColumnContent content={generateContent} twoColumn />
      }
      headerElement={
        <PreviewColumnHeader
          image={
            data?.caseItem.data.requestedRentCategory?.deviceDefinition
              ?.imageURL
          }
          name={generateName}
          onClosePreviewColumn={onClosePreviewColumn}
          productType={
            data?.caseItem.data.requestedRentCategory?.deviceDefinition
              ?.productType
          }
          status={data?.caseItem.status}
          title={generateTitle}
        />
      }
      id={entityId}
      isLoading={isLoading}
      isOpen={isOpen}
      onClosePreviewColumn={onClosePreviewColumn}
      type={PreviewColumnTypeEnum.Request}
      url={path}
    />
  )
}
